import { useRealtimeAgentStore } from "@/shared/store/realtime-agent";
import { getVoiceChunkQueue } from "@/entities/realtime-agent/model/use-chunk-queue";
import { useCallback, useEffect, useRef } from "react";

/**
 * ChunkQueueManager 기반 청크 관리 훅
 *
 * 책임:
 * - ChunkQueueManager에서 청크를 구독하여 currentChunk 상태 업데이트
 * - 오디오 재생 완료 시 다음 청크 소비
 *
 * 흐름:
 * 1. 구독 시: 대기 중인 첫 번째 청크가 있으면 자동 전달됨
 * 2. 청크 도착 시: seq=0이면 자동 전달 (구독자가 있을 때)
 * 3. 오디오 종료 시: consumeNextChunk() → dequeue() → 다음 청크 전달
 */
export const useManageChunks = () => {
  const setCurrentChunk = useRealtimeAgentStore(state => state.setCurrentChunk);
  const isSubscribedRef = useRef(false);

  // ChunkQueueManager 구독
  useEffect(() => {
    if (isSubscribedRef.current) return;

    const queue = getVoiceChunkQueue();

    // 소비자 등록 - 첫 번째 청크가 자동으로 전달됨 (구독 시 또는 enqueue 시)
    const unsubscribe = queue.subscribe((chunk) => {
      setCurrentChunk(chunk);
    });

    isSubscribedRef.current = true;

    return () => {
      unsubscribe();
      isSubscribedRef.current = false;
    };
  }, [setCurrentChunk]);

  /**
   * 다음 청크 소비 (오디오 재생 완료 시 호출)
   */
  const consumeNextChunk = useCallback(() => {
    const queue = getVoiceChunkQueue();
    const chunk = queue.dequeue();

    if (chunk) {
      setCurrentChunk(chunk);
    } else {
      setCurrentChunk(null);
    }
  }, [setCurrentChunk]);

  /**
   * 큐 리셋 (새 대화 시작 시)
   */
  const resetQueueManager = useCallback(() => {
    getVoiceChunkQueue().clear();
  }, []);

  return {
    consumeNextChunk,
    resetQueueManager
  };
};
